/**
 * Shared noise filter for the Datadog browser SDKs (RUM and Logs).
 *
 * Everything dropped here is something nobody can act on: errors raised by
 * mail link scanners and crawlers, by browser extensions, by analytics
 * scripts an ad blocker refused, or by the browser itself. An error thrown by
 * our own code never matches these rules.
 *
 * Wire it in as `beforeSend: rumBeforeSend` on `datadogRum.init` and
 * `beforeSend: logsBeforeSend` on `datadogLogs.init`. Keep this file the same
 * in every app that carries it, so one audit covers all of them.
 */
import type { LogsEvent } from "@datadog/browser-logs";
import type { RumEvent } from "@datadog/browser-rum";

export type ErrorLike = {
  message?: string;
  stack?: string;
  /** Error name (`error.type` in RUM, `error.kind` in Logs). */
  type?: string;
  /** URL of the failed request, when the error is a network failure. */
  url?: string;
};

// One line per sender. The SDK wraps uncaught errors as `Uncaught "..."` and
// manually reported ones as `Provided "..."`, so patterns stay unanchored
// unless the whole message is the point.
const NOISE_MESSAGE_PATTERNS: RegExp[] = [
  // Microsoft Outlook SafeLinks / Defender opening links from our emails:
  // hundreds of errors per visit, all from Microsoft address space.
  /Object Not Found Matching Id:\d+, MethodName:\w+, ParamCount:\d+/,
  // A cross-origin script failed and the browser hid every detail.
  /^(?:Uncaught |Provided )?"?Script error\.?"?$/,
  // The browser's notice that resize callbacks spilled into the next frame.
  /ResizeObserver loop (?:completed with undelivered notifications|limit exceeded)/,
  // Attributes injected into our markup by extensions (Bitdefender's bis_*,
  // accessibility tools), reported by Shopify App Bridge and React.
  /Unexpected attributes on <[^>]+>: /,
  // Crypto wallet extensions fighting over window.ethereum.
  /Failed to connect to MetaMask|Cannot redefine property: ethereum|Cannot set property chainId|chain is not set up|Error initializing provider/,
  // Extension messaging after the extension reloaded or the tab was torn down.
  /Receiving end does not exist|Extension context invalidated|runtime\.sendMessage|Cannot destructure property 'tabId'/,
  // Coupon and cashback extensions.
  /cashbackReminder|Can't find variable: EmptyRanges|EmptyRanges is not defined/,
  // Shadow roots and listeners that extensions attach to our elements.
  /attachShadow|cancelable=false/,
  // Chrome's intervention reports (passive listeners, lazy iframes).
  /^intervention:/,
  // Segment's loader refused by an ad blocker.
  /Failed to load Analytics\.js/,
  // Requests the page itself cancelled (navigation, unmount, AbortController).
  /^AbortError\b|The user aborted a request|signal is aborted without reason|The operation was aborted/,
];

const EXTENSION_URL_PATTERN =
  /(?:chrome|chrome-untrusted|moz|safari|safari-web|ms-browser|edge)-extension:\/\//;

// Analytics, ads and attribution. A failed request to any of these is an ad
// blocker or a privacy setting at work, never a bug in our code.
const THIRD_PARTY_URL_PATTERNS: RegExp[] = [
  /\/\/apicdn\.spocket\.co\//, // Segment, proxied through our own name
  /\/\/(?:api|cdn)\.segment\.(?:io|com)\//,
  /\/\/(?:www\.)?google-analytics\.com\//,
  /\/\/analytics\.google\.com\//,
  /\/\/(?:www\.)?googletagmanager\.com\//,
  /\/\/(?:www\.)?google\.com\/(?:ccm|g|pagead)\//,
  /\/\/[\w.-]*doubleclick\.net\//,
  /\/\/[\w.-]*clarity\.ms\//,
  /\/\/[\w.-]*(?:featureassets\.org|prodregistryv2\.org|statsigapi\.net|statsig\.com)\//, // Statsig
  /\/\/[\w.-]*trygravity\.ai\//,
  /\/\/[\w.-]*(?:pxf\.io|impactcdn\.com)\//, // Impact
  /\/\/[\w.-]*grsm\.io\//, // PartnerStack
  /\/\/get\.(?:spocket\.co|smartli\.ai|alidrop\.co)\//, // PartnerStack on our names
  /\/\/[\w.-]*partnerlinks\.io\//,
  /\/\/[\w.-]*growsumo\.com\//,
  /\/\/[\w.-]*(?:applovin\.com|axon\.ai)\//,
  /\/\/bzr(?:cdn)?\.openai\.com\//, // OpenAI pixel
  /\/\/[\w.-]*facebook\.(?:com|net)\//,
  /\/\/[\w.-]*tiktok\.com\//,
  /\/\/[\w.-]*bing\.com\//,
];

// Search engines and link previews. Datadog still counts them as user
// sessions, and their failures are theirs to fix.
const CRAWLER_UA_PATTERN =
  /Googlebot|bingbot|YandexBot|DuckDuckBot|Baiduspider|YisouSpider|AhrefsBot|SemrushBot|PetalBot|Applebot|facebookexternalhit|HeadlessChrome|Lighthouse|PTST|GTmetrix/i;

// The Logs SDK reports failed requests as "Fetch error POST https://...".
const NETWORK_MESSAGE_PATTERN = /^(?:Fetch|XHR) error \w+ (\S+)/;

export const isThirdPartyUrl = (url: string | undefined): boolean =>
  !!url && THIRD_PARTY_URL_PATTERNS.some((pattern) => pattern.test(url));

// Keep the error when any frame is ours: extensions patch fetch and XHR, so
// their frames turn up inside stacks of errors our code really threw.
export const isExtensionOnlyStack = (stack: string | undefined): boolean => {
  if (!stack || !EXTENSION_URL_PATTERN.test(stack)) return false;
  const origin =
    typeof window !== "undefined" ? window.location.origin : undefined;
  return !origin || !stack.includes(origin);
};

export const isCrawler = (userAgent?: string): boolean => {
  const ua =
    userAgent ?? (typeof navigator !== "undefined" ? navigator.userAgent : "");
  return CRAWLER_UA_PATTERN.test(ua);
};

export const isNoiseError = ({
  message,
  stack,
  type,
  url,
}: ErrorLike): boolean => {
  const text = message ?? "";
  if (type === "AbortError") return true;
  if (NOISE_MESSAGE_PATTERNS.some((pattern) => pattern.test(text))) {
    return true;
  }
  if (isThirdPartyUrl(url)) return true;
  if (isThirdPartyUrl(NETWORK_MESSAGE_PATTERN.exec(text)?.[1])) return true;
  return isExtensionOnlyStack(stack);
};

/** `beforeSend` for `datadogRum.init`. Only error events are ever dropped. */
export const rumBeforeSend = (event: RumEvent): boolean => {
  if (event.type !== "error") return true;
  if (isCrawler()) return false;
  const { error } = event;
  return !isNoiseError({
    message: error.message,
    stack: error.stack,
    type: error.type,
    url: error.resource?.url,
  });
};

/** `beforeSend` for `datadogLogs.init`. */
export const logsBeforeSend = (event: LogsEvent): boolean => {
  if (isCrawler()) return false;
  return !isNoiseError({
    message: event.message,
    stack: event.error?.stack,
    type: event.error?.kind,
    url: event.http?.url,
  });
};
